import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Notice, StatusBadge, latestRun, relativeTime } from '../components';
import type { Client, ConnectionHealth } from '../types';

export function Dashboard({ onSignedOut }: { onSignedOut: () => void }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [health, setHealth] = useState<ConnectionHealth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', timezone: 'UTC' });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [clientList, healthRows] = await Promise.all([api.listClients(), api.health()]);
      setClients(clientList);
      setHealth(healthRows);
      setError('');
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized) {
        onSignedOut();
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [onSignedOut]);

  useEffect(() => {
    void load();
  }, [load]);

  async function createClient(event: React.FormEvent) {
    event.preventDefault();
    setCreating(true);
    setError('');
    try {
      const created = await api.createClient(form);
      // The generated password exists only in this response, so hand it
      // straight to the detail page, which shows it once.
      navigate(
        `/clients/${created.client.slug}?password=${encodeURIComponent(
          created.looker.password ?? '',
        )}`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create client');
      setCreating(false);
    }
  }

  async function signOut() {
    await api.logout();
    onSignedOut();
  }

  // Derive the slug from the display name until the operator edits it.
  function setName(name: string) {
    setForm((prev) => ({
      ...prev,
      name,
      slug: prev.slug === slugify(prev.name) ? slugify(name) : prev.slug,
    }));
  }

  const byClient = new Map<number, ConnectionHealth[]>();
  for (const row of health) {
    if (!row.connection_id) continue;
    byClient.set(row.client_id, [...(byClient.get(row.client_id) ?? []), row]);
  }

  return (
    <>
      <header className="top">
        <div>
          <h1>Social Reporting</h1>
          <div className="sub">
            {clients.length} client{clients.length === 1 ? '' : 's'} &middot; Instagram +
            LinkedIn into Looker Studio
          </div>
        </div>
        <button onClick={signOut}>Sign out</button>
      </header>

      {error && <Notice kind="err">{error}</Notice>}

      <div className="card">
        <h2>Clients</h2>
        {loading ? (
          <div className="spinner">Loading…</div>
        ) : clients.length === 0 ? (
          <p className="muted">No clients yet. Add one below to provision its schema.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Connections</th>
                  <th>Last sync</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => {
                  const rows = byClient.get(client.id) ?? [];
                  return (
                    <tr key={client.id}>
                      <td>
                        <Link to={`/clients/${client.slug}`}>
                          <strong>{client.name}</strong>
                        </Link>
                        <div className="muted mono">client_{client.slug}</div>
                      </td>
                      <td>
                        {rows.length === 0 ? (
                          <span className="badge idle">none</span>
                        ) : (
                          rows.map((row) => (
                            <div key={row.connection_id} style={{ marginBottom: 4 }}>
                              <StatusBadge
                                status={row.status}
                                lastRunStatus={row.last_run_status}
                              />{' '}
                              <span className="muted">
                                {row.platform} &middot; {row.external_name ?? '—'}
                              </span>
                            </div>
                          ))
                        )}
                      </td>
                      <td className="muted">{relativeTime(latestRun(rows))}</td>
                      <td>
                        <Link className="btn" to={`/clients/${client.slug}`}>
                          Manage
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>Add a client</h2>
        <p className="muted" style={{ marginTop: -6 }}>
          Creates a dedicated Postgres schema and a read-only role. The Looker Studio password
          is generated here and shown once.
        </p>
        <form onSubmit={createClient}>
          <div className="row">
            <div className="field">
              <label htmlFor="name">Display name</label>
              <input
                id="name"
                required
                placeholder="Altitude Accelerator"
                value={form.name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="slug">Slug</label>
              <input
                id="slug"
                required
                pattern="[a-z][a-z0-9_]{1,38}"
                title="Lowercase letters, digits and underscores; must start with a letter."
                placeholder="altitude"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="timezone">Timezone</label>
              <input
                id="timezone"
                placeholder="America/Toronto"
                value={form.timezone}
                onChange={(e) => setForm({ ...form, timezone: e.target.value })}
              />
            </div>
            <div className="field shrink">
              <button className="primary" type="submit" disabled={creating}>
                {creating ? 'Creating…' : 'Create client'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, 'c$1')
    .slice(0, 39);
}
